import { useOfflineModeManager } from 'FrontRoyalAngular';
import { memo, useMemo, type FC } from 'react';
import { StorableImage } from 'StorableImage/StorableImage';
import { twMerge } from 'tailwind-merge';
import lockedIcon from 'vectors/track_icon_locked.svg';

type IconSize = 7.5 | 10 | 12;

// Full class strings, so Tailwind finds them when it scans the source
const sizeClasses: Record<IconSize, string> = { 7.5: 'h-7.5 w-7.5', 10: 'h-10 w-10', 12: 'h-12 w-12' };
const desktopSizeClasses: Record<IconSize, string> = {
    7.5: 'md:h-7.5 md:w-7.5',
    10: 'md:h-10 md:w-10',
    12: 'md:h-12 md:w-12',
};

// See comment near frontRoyalStoreApi/getStorableImage for an explanation of which
// images get cached forever and which do not. The default has to be false because we want
// it to be false for lesson images, and those are hard to change because they are included in
// the lesson content.
type Props = {
    src: string;
    size?: IconSize;
    desktopSize?: IconSize;
    alt: string;
    cacheForever: boolean;
    fadeInOnLoad?: boolean;
    availableOffline?: boolean;
};

const getClassName = (size: IconSize, desktopSize: Props['desktopSize']) =>
    twMerge('inline-flex', sizeClasses[size], desktopSize && desktopSizeClasses[desktopSize]);

export const ContentItemIcon: FC<Props> = memo(
    ({ src, size = 10, desktopSize, alt, cacheForever = false, fadeInOnLoad = true, availableOffline = false }) => {
        const { inOfflineMode } = useOfflineModeManager();
        const className = useMemo(() => getClassName(size, desktopSize), [size, desktopSize]);

        const showLockedIcon = !availableOffline && inOfflineMode;
        const imageSrc = useMemo(() => {
            if (showLockedIcon) return lockedIcon;

            return src;
        }, [showLockedIcon, src]);

        return (
            <StorableImage
                src={imageSrc}
                alt={alt}
                cacheForever={cacheForever && !showLockedIcon}
                fadeInOnLoad={fadeInOnLoad}
                className={className}
            />
        );
    },
);
